import React, { useState } from 'react';
import { Modal, Typography, Button, Input, Form, useToast } from '@crisellevaldez/speculo-ui';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../../lib/firebase';

interface DomainModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (subdomain: string) => void;
  currentSubdomain?: string;
}

const DomainModal: React.FC<DomainModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  currentSubdomain,
}) => {
  const [subdomain, setSubdomain] = useState(currentSubdomain || '');
  const [isChecking, setIsChecking] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const { showToast } = useToast();

  const validateSubdomain = (value: string) => {
    // Only allow lowercase letters, numbers, and hyphens
    // Must start and end with alphanumeric
    const regex = /^[a-z0-9][a-z0-9-]*[a-z0-9]$/;
    return regex.test(value);
  };

  const checkSubdomainAvailability = async (value: string) => {
    if (!validateSubdomain(value)) {
      setError(
        'Subdomain must contain only lowercase letters, numbers, and hyphens, and must start and end with alphanumeric characters'
      );
      return false;
    }

    setIsChecking(true);
    setError('');

    try {
      const organizationsRef = collection(db, 'Organizations');
      const q = query(organizationsRef, where('subdomain', '==', value));
      const snapshot = await getDocs(q);

      // If we found any documents with this subdomain (except current one)
      const isAvailable = snapshot.empty || (currentSubdomain && value === currentSubdomain);

      if (!isAvailable) {
        setError('This subdomain is already taken');
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error checking subdomain:', error);
      setError('Error checking subdomain availability');
      return false;
    } finally {
      setIsChecking(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!subdomain) {
      setError('Subdomain is required');
      return;
    }

    try {
      setIsSaving(true);
      const isAvailable = await checkSubdomainAvailability(subdomain);
      if (isAvailable) {
        await onSuccess(subdomain);
        onClose();
        showToast({
          message: 'Domain updated successfully',
          variant: 'success',
        });
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <form onSubmit={handleSubmit}>
        <Modal.Header>
          <Typography variant="h3">
            Update Domain
          </Typography>
        </Modal.Header>
        <Modal.Body>
          <div>
            <div className="space-y-4">
              <Typography variant="small" className="text-gray-500 dark:text-gray-400 mb-4">
                Choose a subdomain for your landing page. This will be used to create your unique
                URL.
              </Typography>
              <Form.Control required>
                <Form.Label>Subdomain</Form.Label>
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-2">
                    <Input
                      value={subdomain}
                      onChange={(e) => {
                        setSubdomain(e.target.value.toLowerCase());
                        setError('');
                      }}
                      placeholder="your-subdomain"
                    />
                    <Typography
                      variant="small"
                      className="text-gray-500 dark:text-gray-400 whitespace-nowrap self-center"
                    >
                      .signfast.ai
                    </Typography>
                  </div>
                  {error && (
                    <Typography variant="small" className="text-red-500">
                      {error}
                    </Typography>
                  )}
                </div>
              </Form.Control>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            isLoading={isChecking || isSaving}
            disabled={isChecking || isSaving || !subdomain}
          >
            Save Changes
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};

export { DomainModal };
